import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Table from "components/Common/dataTable/index";
import { StudentType } from "types/studentTypes";
import { GENDER_OPTIONS } from "constants/userConstants";
import { all_routes } from "router/all_routes";
import { useAbility } from "config/abilityContext";
import CustomAlert from "components/Common/ui/CustomAlert";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";

interface TeacherStudentTableProps {
    students: StudentType[];
    isLoading: boolean;
    pagination: {
        page: number;
        limit: number;
        total: number;
    };
    error: string | null;
    onChange: (pg: any) => void;
    openStudentDetails: (record: any) => void
}

const TeacherStudentTable: React.FC<TeacherStudentTableProps> = ({
    students,
    isLoading,
    pagination,
    error,
    onChange,
    openStudentDetails,
}) => {
    const { t } = useTranslation();
    const ability = useAbility();
    const routes = all_routes;

    const translatedGender = GENDER_OPTIONS.map(gender => ({
        ...gender,
        label: t(gender.label)
    }));

    const columns = [
        {
            title: t("first_name"),
            dataIndex: ["user", "firstName"],
            sorter: false,
        },
        {
            title: t("last_name"),
            dataIndex: ["user", "lastName"],
            sorter: false,
        },
        {
            title: t("email"),
            dataIndex: ["user", "email"],
            render: (_: any, record: StudentType) => (
                <Link
                    to={ability.can("getAll", "Student") ?
                        routes.studentDetails.replace(":id", record.id) + routes.studentDetailsTabs.details : "#"}
                    className="link-primary"
                >
                    {record.user?.email || "-"}
                </Link>
            ),
            sorter: false,
        },
        {
            title: t("class"),
            dataIndex: ["class", "name"],
            render: (_: string, record: StudentType) => record.class?.name || "-",
            sorter: false,
        },
        {
            title: t("gender"),
            dataIndex: "gender",
            render: (_: string, record: StudentType) => {
                return translatedGender.find(option => option.value === record.user?.gender)?.label || "-";
            },
            sorter: false,
        },
        {
            title: t("massar_code"),
            dataIndex: "massarCode",
            render: (text: string) => text || "-",
            sorter: false,
        },
        {
            title: t("action"),
            dataIndex: "action",
            render: (_: string, record: StudentType) => {
                const actions: Action[] = [

                    {
                        label: t("show"),
                        icon: "ti ti-eye",
                        actionType: "modal",
                        hidden: !ability.can("read", "Student"),
                        onClick: () => openStudentDetails(record),

                    },
                ];
                return <ActionsDropdown actions={actions} />;

            },
        },
    ];


    return (
        <div className="table-responsive">
            {error ? (
                <CustomAlert
                    message={error}
                    type="error"
                    showIcon
                />
            ) : (
                <Table
                    loading={isLoading}
                    dataSource={students}
                    columns={columns}
                    dataPaginate={{
                        current: pagination?.page || 1,
                        pageSize: pagination?.limit || 10,
                        total: pagination?.total || 0,
                    }}
                    pagination={true}
                    onChange={onChange}
                />)}
        </div>
    );
};

export default TeacherStudentTable;